import type { FC } from 'react';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMinusCircle, FaPlusCircle } from 'react-icons/fa';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';

import { useBusinessUnits } from 'src/components/app/context/BusinessUnitsContext';
import type { FilterContextValue } from 'src/components/app/context/FilterContext';
import {
  FilterContext,
  useFilterContext,
} from 'src/components/app/context/FilterContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import { mapRoleToProps } from 'src/components/app/context/UserContext/helper';
import useRolesQuery from 'src/components/account/hooks/useRolesQuery';
import type {
  FilterNode,
  FilterTree,
  FilterTreeOption,
} from 'src/components/userAdmin/UserListPage/filterTree';
import {
  buildFilterTree,
  constructNodeId,
  expandedNodes,
} from 'src/components/userAdmin/UserListPage/filterTree';

import { styles } from './index.styles';

interface FilterNodeItemProps {
  nodeId: string;
  node: FilterNode;
  parentNodeId: string;
  parentNodes: FilterNode[];
}

interface TreeFilterProps {
  filterOptions?: FilterTreeOption[];
  filterState: FilterContextValue;
  applyFilter: () => void;
  removeFilter: () => void;
}

const FilterNodeItem: FC<FilterNodeItemProps> = ({
  nodeId,
  node,
  parentNodeId,
  parentNodes = [],
}) => {
  const { t } = useTranslation('admin');
  const {
    filterGroups: selectedFilterGroups,
    getSpec,
    toggleFilter,
  } = useFilterContext();

  if ('filterExpr' in node) {
    const spec = getSpec(parentNodeId);
    const multiple = spec ? spec.multiple : true;
    const checked =
      selectedFilterGroups[parentNodeId]?.findIndex(
        (filterValue) => filterValue.label === node.label,
      ) >= 0;

    const control = multiple ? (
      <Checkbox
        checked={checked}
        onClick={() => toggleFilter(parentNodeId, node)}
        size="small"
      />
    ) : (
      <Radio
        checked={checked}
        onClick={() => toggleFilter(parentNodeId, node)}
        size="small"
      />
    );

    return (
      <TreeItem
        key={nodeId}
        label={<FormControlLabel control={control} label={t(node.label)} />}
        itemId={nodeId}
      />
    );
  }

  return (
    <TreeItem
      key={nodeId}
      label={<Typography sx={{ padding: 1 }}>{t(node.name)}</Typography>}
      itemId={nodeId}
    >
      {node.childNodes.map((node) => {
        const childNodeId = constructNodeId([...parentNodes, node]);

        return (
          <FilterNodeItem
            key={childNodeId}
            node={node}
            nodeId={childNodeId}
            parentNodeId={nodeId}
            parentNodes={[...parentNodes, node]}
          />
        );
      })}
    </TreeItem>
  );
};

export const TreeFilter: FC<TreeFilterProps> = ({
  filterOptions,
  filterState,
  applyFilter,
  removeFilter,
}) => {
  const { t } = useTranslation('admin');
  const [expanded, setExpanded] = useState<string[]>(
    expandedNodes(Object.keys(filterState.filterGroups)),
  );
  const onNodeToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
    setExpanded(nodeIds);
  };
  const { user } = useUserContext();
  const { businessUnits } = useBusinessUnits();
  const { data: roles } = useRolesQuery();
  const filterTree: FilterTree = user
    ? buildFilterTree(
        user,
        businessUnits,
        (roles ?? []).map(mapRoleToProps),
        filterOptions,
      )
    : [];

  return (
    <FilterContext.Provider value={filterState}>
      <Stack sx={styles.treeWrapper}>
        <Box sx={styles.treeContainer}>
          <SimpleTreeView
            slots={{
              expandIcon: () => <FaPlusCircle size={20} />,
              collapseIcon: () => <FaMinusCircle size={20} />,
            }}
            expandedItems={expanded}
            onExpandedItemsChange={onNodeToggle}
            sx={styles.treeView}
          >
            {filterTree.map((node) => {
              const childNodeId = constructNodeId([node]);

              return (
                <FilterNodeItem
                  key={childNodeId}
                  node={node}
                  nodeId={childNodeId}
                  parentNodeId=""
                  parentNodes={[node]}
                />
              );
            })}
          </SimpleTreeView>
        </Box>
        <Box sx={styles.buttonContainer}>
          <Stack direction="row" spacing={1}>
            <Button
              variant="cta"
              color="secondary"
              fullWidth
              onClick={removeFilter}
              sx={styles.button}
            >
              {t('admin:Filter.DeleteSelection')}
            </Button>
            <Button
              variant="cta"
              fullWidth
              onClick={() => {
                applyFilter();
                setExpanded([]);
              }}
              sx={styles.button}
            >
              {t('common:apply')}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </FilterContext.Provider>
  );
};
